export const getAuthErrorMessage = (error) => {
  const errorCode = error.code || error.message;

  const errorMessages = {
    // Authentication errors
    'auth/invalid-login-credentials':
      'Invalid email or password. Please try again.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled':
      'This account has been disabled. Please contact support.',
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password': 'Password should be at least 6 characters.',
    'auth/operation-not-allowed':
      'Email/password accounts are not enabled. Please contact support.',
    'auth/invalid-credential': 'Invalid email or password. Please try again.',

    // Google Sign-in errors
    'auth/popup-closed-by-user': 'Sign-in was cancelled. Please try again.',
    'auth/cancelled-popup-request': 'Another sign-in popup is already open.',
    'auth/popup-blocked':
      'Sign-in popup was blocked. Please allow popups and try again.',
    'auth/account-exists-with-different-credential':
      'An account already exists with this email using a different sign-in method.',

    // Password reset errors
    'auth/missing-email': 'Please enter your email address.',
    'auth/invalid-action-code':
      'This password reset link is invalid or has expired.',
    'auth/expired-action-code':
      'This password reset link has expired. Please request a new one.',

    // Network errors
    'auth/network-request-failed':
      'Network error. Please check your connection and try again.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/app-deleted':
      'Authentication service error. Please refresh the page.',

    // Generic errors
    'auth/internal-error': 'An unexpected error occurred. Please try again.',
    'auth/invalid-api-key':
      'Application configuration error. Please contact support.',
  };

  // Check if we have a custom message for this error code
  if (errorMessages[errorCode]) {
    return errorMessages[errorCode];
  }

  // If the error message contains "Firebase: Error", clean it up
  if (errorCode.includes('Firebase: Error')) {
    return (
      errorCode
        .replace('Firebase: Error ', '')
        .replace(/\(auth\/[^)]+\)\.?/, '')
        .trim() || 'An error occurred. Please try again.'
    );
  }

  // Return the original message if we don't have a mapping
  return error.message || 'An error occurred. Please try again.';
};

/**
 * Why an invitation could not be accepted, in words the invitee can act on.
 *
 * `acceptProjectInvitation` rejects with an `HttpsError`, whose `code` names
 * the reason. Signup used to swallow all of them and show the preview-code
 * screen instead, which asks the invitee for a code nobody ever issued them —
 * and hides the real cause, including an invitation that was raised in a
 * different environment from the one the link points at.
 */
export const getInvitationErrorMessage = (error) => {
  const code = (error?.code || '').replace(/^functions\//, '');

  const messages = {
    'not-found':
      "We couldn't find this invitation. Ask whoever invited you to send a new one.",
    'deadline-exceeded':
      'This invitation has expired. Ask whoever invited you to resend it.',
    'already-exists':
      'This invitation has already been used. Ask whoever invited you to send a new one.',
    'permission-denied':
      'This invitation was cancelled. Ask whoever invited you to send a new one.',
    'invalid-argument':
      'This invitation link is not valid. Ask whoever invited you to send a new one.',
    unauthenticated: 'Please sign in again to accept this invitation.',
  };

  return (
    messages[code] ||
    "We couldn't accept this invitation just now. Please try again."
  );
};
